import type {
  DerivedRecord,
  IngestBatch,
  IngestItem,
  IngestResult,
  MaterialRecord,
  PoolSummary,
  Provenance,
  TimeAxis,
} from '@capsio/contracts';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, materialContentUrl, messageOf, uploadIngestFile } from '../api-client.js';

/**
 * 原始记录池。
 *
 * **不是一个上传页面，是跨 ATC 的基础设施**——调研 ATC 用它，运营 ATC 也用它
 * （客户上传订单、图纸、来料单）。所以它是一个独立去处，不挂在任何一张卡下面。
 *
 * 四件事：载入、时间轴、检索、台账。
 */

const CARRIER_LABEL: Record<string, string> = {
  conversation: '对话记录',
  spreadsheet: '表格',
  document: '文档',
  drawing: '图纸',
  formal_document: '正式单据',
  image: '图片',
  archive: '归档包',
};

const PROVENANCE_LABEL: Record<string, string> = {
  client_provided: '客户提供',
  provider_produced: '我方产出',
};

/**
 * 时间出处的中文与**可信度**。
 *
 * 界面上必须显示这一栏：EXIF 是相机写的事实，文件名是推断（谁都能改文件名），
 * 修改时间更弱（复制一次就变）。三者精度天差地别，
 * 不标出来人就会一视同仁地当作「这份东西的时间」用。
 */
const TIME_SOURCE: Record<string, { label: string; strong: boolean }> = {
  exif: { label: '拍摄信息', strong: true },
  office_meta: { label: '文档属性', strong: true },
  filename: { label: '文件名', strong: false },
  embedded_doc: { label: '所在文档', strong: false },
  file_mtime: { label: '修改时间', strong: false },
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatTime(iso: string | null, withYear = false): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const date = `${withYear ? `${at.getFullYear()}-` : ''}${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `${date} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** 同一天的记录归到一组。时间轴的基本形状是「哪一天发生了什么」。 */
function dayKeyOf(row: MaterialRecord, axis: TimeAxis): string {
  const iso = axis === 'content' ? (row.contentTime ?? row.uploadedAt) : row.uploadedAt;
  const at = new Date(iso);
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────
// 载入
// ─────────────────────────────────────────────────────────────────

interface UploadRow {
  name: string;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  result?: IngestResult;
  error?: string;
}

function IntakePanel({ onDone }: { onDone: () => void }): ReactNode {
  const [label, setLabel] = useState('');
  /*
   * **来源无默认值**（C-125 二.1）。
   *
   * 原初值是 `client_provided`。那个默认与脚本时代路径启发式的**最坏答案是同一个值**——
   * 混批时不动这个下拉，我方产出会被整批标成客户提供，而没有任何地方会报警。
   * 空串即「还没选」，未选之前不让传。
   */
  const [provenance, setProvenance] = useState<Provenance | ''>('');
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const dirInput = useRef<HTMLInputElement | null>(null);

  const send = useCallback(
    async (files: File[]): Promise<void> => {
      if (files.length === 0 || busy) return;
      // 未选来源即拒，连批都不开——不猜，也不让人以为传成了。
      if (provenance === '') {
        setError('请先选「来源」：这一批是客户提供，还是我方产出。来源不设默认值。');
        return;
      }
      setBusy(true);
      setError(null);
      setRows(files.map((file) => ({ name: file.name, status: 'pending' })));

      try {
        const { batchId } = await api.openIngestBatch({
          label: label.trim() === '' ? undefined : label.trim(),
          provenance,
        });

        /*
         * **一份一份发，不并发。**
         *
         * 并发会让同一批里内容相同的两份同时走到「查去重 → 没有 → 插入」之间，
         * 两个都判定为新，然后第二个撞上 content_hash 唯一索引报错——
         * 一次本该是「去重命中」的事变成一次失败。
         * 逐份发慢一点，但结果与脚本导入完全一致（硬约束）。
         */
        for (const [index, file] of files.entries()) {
          setRows((previous) =>
            previous.map((row, i) => (i === index ? { ...row, status: 'uploading' } : row)),
          );
          try {
            const result = await uploadIngestFile(batchId, file, provenance);
            setRows((previous) =>
              previous.map((row, i) =>
                i === index
                  ? { ...row, status: result.outcome === 'failed' ? 'failed' : 'done', result }
                  : row,
              ),
            );
          } catch (cause) {
            setRows((previous) =>
              previous.map((row, i) =>
                i === index ? { ...row, status: 'failed', error: messageOf(cause) } : row,
              ),
            );
          }
        }
        onDone();
      } catch (cause) {
        setError(messageOf(cause));
      } finally {
        setBusy(false);
      }
    },
    [busy, label, provenance, onDone],
  );

  const done = rows.filter((row) => row.status === 'done' || row.status === 'failed').length;
  const stored = rows.filter((row) => row.result?.outcome === 'stored').length;
  const deduped = rows.filter((row) => row.result?.outcome === 'deduplicated').length;
  const failed = rows.filter((row) => row.status === 'failed').length;
  const media = rows.reduce((sum, row) => sum + (row.result?.mediaNew ?? 0), 0);

  return (
    <section className="ing-intake">
      <div className="ing-intake-form">
        <label className="field">
          <span>这批是什么</span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="宁波玉龙 8 月询盘"
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>来源</span>
          <select
            value={provenance}
            onChange={(event) => setProvenance(event.target.value as Provenance | '')}
            disabled={busy}
            aria-invalid={provenance === ''}
          >
            <option value="">请选择……</option>
            <option value="client_provided">客户提供</option>
            <option value="provider_produced">我方产出</option>
          </select>
        </label>
      </div>
      {/*
        来源必须由人选，不能猜。猜错的后果是把我方结论当客户原话引用——
        归属判定与审计会因此建立在假前提上。
      */}
      <p className="fineprint">
        来源要选对：把我方产出标成客户提供，之后的归属判定与审计就建立在假前提上。
        <strong>不设默认值——不选不让传。</strong>
        <br />
        <strong>一批一个来源，混合来源请分批上传。</strong>
        逐份选会让 50 份变成 50 次选择，而分批本来就是自然的工作方式。
      </p>

      <div
        className={`ing-drop${dragging ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void send([...event.dataTransfer.files]);
        }}
      >
        <p className="ing-drop-main">
          {provenance === '' ? '先在上面选「来源」' : '把文件拖到这里'}
        </p>
        <p className="sub-hint">
          上传即算内容哈希去重、即提取 docx / xlsx 里的内嵌图片——与命令行导入完全一致。
        </p>
        <div className="ing-drop-actions">
          <button
            className="btn"
            type="button"
            disabled={busy || provenance === ''}
            onClick={() => fileInput.current?.click()}
          >
            选文件
          </button>
          <button
            className="btn ghost"
            type="button"
            disabled={busy || provenance === ''}
            onClick={() => dirInput.current?.click()}
          >
            选整个文件夹
          </button>
        </div>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            void send([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
        <input
          ref={dirInput}
          type="file"
          multiple
          hidden
          // 目录结构本身是信息，浏览器用这个属性给相对路径。
          {...{ webkitdirectory: '' }}
          onChange={(event) => {
            void send([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
      </div>

      {error !== null && <p className="msg">{error}</p>}

      {rows.length > 0 && (
        <div className="ing-progress">
          <p className="ing-progress-head">
            {busy ? `导入中 ${done}／${rows.length}` : `完成 ${rows.length} 份`}
            {!busy && (
              <span className="sub-hint">
                新入库 {stored} 去重 {deduped} 失败 {failed}
                {media > 0 ? `  提取图片 ${media}` : ''}
              </span>
            )}
          </p>
          <ul className="ing-progress-list">
            {rows.map((row, index) => (
              <li key={`${row.name}-${index}`} className={`ing-progress-row is-${row.status}`}>
                <span className="ing-progress-name">{row.name}</span>
                <span className="ing-progress-note">
                  {row.status === 'pending' && '等待'}
                  {row.status === 'uploading' && '上传中'}
                  {row.status === 'failed' && (row.error ?? row.result?.failureReason ?? '失败')}
                  {row.status === 'done' && row.result !== undefined && (
                    <>
                      {row.result.outcome === 'stored' ? '已入库' : '内容已在库（去重）'}
                      {row.result.mediaFound > 0 &&
                        `  内嵌图 ${row.result.mediaFound}／新 ${row.result.mediaNew}`}
                      {row.result.extraction === 'unsupported' && '  老格式，提取不支持'}
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// 时间轴 + 检索
// ─────────────────────────────────────────────────────────────────

function TimeCell({ row, axis }: { row: MaterialRecord; axis: TimeAxis }): ReactNode {
  const source = row.contentTimeSource === null ? undefined : TIME_SOURCE[row.contentTimeSource];
  return (
    <div className="ing-times">
      <span className={axis === 'uploaded' ? 'ing-time is-primary' : 'ing-time'}>
        <span className="ing-time-tag">导入</span> {formatTime(row.uploadedAt)}
      </span>
      <span className={axis === 'content' ? 'ing-time is-primary' : 'ing-time'}>
        <span className="ing-time-tag">资料</span> {formatTime(row.contentTime)}
        {source !== undefined && (
          /* 依据强度写在时间旁边：推断出来的时间不能长得像事实。 */
          <span className={source.strong ? 'ing-src is-strong' : 'ing-src'}>{source.label}</span>
        )}
      </span>
    </div>
  );
}

function DerivedList({ materialId }: { materialId: string }): ReactNode {
  const [rows, setRows] = useState<DerivedRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.ingestDerived(materialId).then(
      (data) => setRows(data.rows),
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, [materialId]);

  if (error !== null) return <p className="msg">{error}</p>;
  if (rows === null) return <p className="sub-hint">读取中……</p>;

  return (
    <ul className="ing-derived">
      {rows.map((row) => (
        <li key={row.materialId}>
          <a href={materialContentUrl(row.materialId)} target="_blank" rel="noreferrer">
            <img src={materialContentUrl(row.materialId)} alt={row.originalName ?? row.entryPath} />
          </a>
          <span className="ing-derived-meta">
            {row.locator ?? row.entryPath}
            {!row.referenced && <span className="chip">正文未引用</span>}
            {row.alsoIn > 0 && <span className="chip">另出现在 {row.alsoIn} 份文档</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TimelineView({ reloadKey }: { reloadKey: number }): ReactNode {
  const [axis, setAxis] = useState<TimeAxis>('uploaded');
  const [q, setQ] = useState('');
  const [carrier, setCarrier] = useState<string[]>([]);
  const [provenance, setProvenance] = useState('');
  const [customer, setCustomer] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<MaterialRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      void api
        .ingestTimeline({
          axis,
          q,
          carrier,
          provenance: provenance === '' ? undefined : provenance,
          customer,
          // 日期框给的是当地日历日；到点那一头要含整天，否则「筛 5 月 25 日」会漏掉当天下午。
          from: from === '' ? undefined : new Date(`${from}T00:00:00`).toISOString(),
          to: to === '' ? undefined : new Date(`${to}T23:59:59.999`).toISOString(),
          limit: 100,
        })
        .then(
          (page) => {
            setRows(page.rows);
            setTotal(page.total);
            setError(null);
          },
          (cause: unknown) => setError(messageOf(cause)),
        );
    }, 200); // 输入关键词时防抖，别每敲一个字打一次库
    return () => clearTimeout(timer);
  }, [axis, q, carrier, provenance, customer, from, to, reloadKey]);

  const toggleCarrier = (kind: string): void =>
    setCarrier((previous) =>
      previous.includes(kind) ? previous.filter((k) => k !== kind) : [...previous, kind],
    );

  /*
   * 日期分组在渲染前算好。**不在 map 里改一个外部变量**——
   * React 的渲染可以被中断与重放，那种写法在重放时会给出与上一次不同的结果。
   */
  const grouped = rows.map((row, index) => {
    const day = dayKeyOf(row, axis);
    return { row, day, newDay: index === 0 || day !== dayKeyOf(rows[index - 1]!, axis) };
  });

  return (
    <>
      <div className="ing-filters">
        <input
          className="ing-search"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="搜文件名、原始路径、批次说明"
        />
        <input
          value={customer}
          onChange={(event) => setCustomer(event.target.value)}
          placeholder="客户"
          title="V1 还没有客户实体，按原始路径与批次说明匹配"
        />
        <select value={provenance} onChange={(event) => setProvenance(event.target.value)}>
          <option value="">全部来源</option>
          <option value="client_provided">客户提供</option>
          <option value="provider_produced">我方产出</option>
        </select>
        <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        <span className="sub-hint">至</span>
        <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />

        {/*
          两根时间轴的切换。**这不是排序偏好，是在问两个不同的问题**：
          「上周导了什么」与「玉龙那次沟通有哪些记录」。
        */}
        <div className="segmented ing-axis" role="tablist" aria-label="时间维度">
          <button
            type="button"
            role="tab"
            aria-selected={axis === 'uploaded'}
            onClick={() => setAxis('uploaded')}
          >
            按导入时间
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={axis === 'content'}
            onClick={() => setAxis('content')}
          >
            按资料时间
          </button>
        </div>
      </div>

      <div className="ing-chips">
        {Object.entries(CARRIER_LABEL).map(([kind, label]) => (
          <button
            key={kind}
            type="button"
            className={carrier.includes(kind) ? 'chip is-on' : 'chip'}
            onClick={() => toggleCarrier(kind)}
          >
            {label}
          </button>
        ))}
      </div>

      {error !== null && <p className="msg">{error}</p>}

      <p className="sub-hint ing-count">
        {total} 条{rows.length < total ? `，显示前 ${rows.length} 条` : ''}
      </p>

      <ul className="ing-stream">
        {grouped.map(({ row, day, newDay }) => {
          const open = openId === row.materialId;
          return (
            <li key={row.materialId}>
              {newDay && <p className="ing-day">{day}</p>}
              <div className="ing-row">
                <div className="ing-row-main">
                  <a
                    className="ing-name"
                    href={materialContentUrl(row.materialId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {row.originalName ?? '（无名）'}
                  </a>
                  <div className="ing-meta">
                    <span className="chip">
                      {CARRIER_LABEL[row.carrierKind] ?? row.carrierKind}
                    </span>
                    <span className="chip">
                      {PROVENANCE_LABEL[row.provenance] ?? row.provenance}
                    </span>
                    <span className="sub-hint">{formatBytes(row.byteSize)}</span>
                    {row.batchLabel !== null && <span className="sub-hint">{row.batchLabel}</span>}
                  </div>
                  {row.sourcePath !== null && row.sourcePath !== row.originalName && (
                    <p className="ing-path mono">{row.sourcePath}</p>
                  )}
                  {/*
                    去重命中时本体的名字停在第一次入库那个（常常是 imageN.png），
                    后来单独提交的名字才有信息量——列出来，否则人只看到一串 image3.png。
                  */}
                  {row.alsoNamed.length > 0 && (
                    <p className="ing-aka sub-hint">又名 {row.alsoNamed.join('、')}</p>
                  )}
                  {row.derivedCount > 0 && (
                    <button
                      className="linkbtn"
                      type="button"
                      onClick={() => setOpenId(open ? null : row.materialId)}
                    >
                      {open ? '收起' : `含 ${row.derivedCount} 张内嵌图片`}
                    </button>
                  )}
                </div>
                <TimeCell row={row} axis={axis} />
              </div>
              {open && <DerivedList materialId={row.materialId} />}
            </li>
          );
        })}
      </ul>

      {rows.length === 0 && error === null && <p className="sub-hint">没有匹配的记录。</p>}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// 台账
// ─────────────────────────────────────────────────────────────────

function BatchItems({ batchId }: { batchId: string }): ReactNode {
  const [rows, setRows] = useState<IngestItem[] | null>(null);
  useEffect(() => {
    void api.ingestBatchItems(batchId).then(
      (data) => setRows(data.rows),
      () => setRows([]),
    );
  }, [batchId]);
  if (rows === null) return <p className="sub-hint">读取中……</p>;

  return (
    <ul className="ing-items">
      {rows.map((row) => (
        <li key={row.itemId} className={`is-${row.outcome}`}>
          <span className="mono">{row.sourcePath ?? row.submittedName}</span>
          <span className="sub-hint">
            {row.outcome === 'stored' && '新入库'}
            {row.outcome === 'deduplicated' && '去重命中'}
            {row.outcome === 'failed' && `失败：${row.failureReason ?? '（无说明）'}`}
            {row.mediaFound > 0 && `  内嵌图 ${row.mediaFound}／新 ${row.mediaNew}`}
            {row.extraction === 'unsupported' && '  老格式，提取不支持'}
          </span>
        </li>
      ))}
    </ul>
  );
}

function LedgerView({ reloadKey }: { reloadKey: number }): ReactNode {
  const [rows, setRows] = useState<IngestBatch[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    void api.ingestLedger(50, 0).then(
      (page) => {
        setRows(page.rows);
        setError(null);
      },
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, [reloadKey]);

  if (error !== null) return <p className="msg">{error}</p>;
  if (rows.length === 0) return <p className="sub-hint">还没有导入记录。</p>;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>时间</th>
            <th>这批是什么</th>
            <th>谁导的</th>
            <th>从哪</th>
            <th>份数</th>
            <th>新入库</th>
            <th>去重</th>
            <th>失败</th>
            <th>内嵌图</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.batchId} className={openId === row.batchId ? 'is-open' : undefined}>
              <td>{formatTime(row.startedAt, true)}</td>
              <td>{row.label ?? '—'}</td>
              <td>{row.actor}</td>
              <td>{row.channel === 'web_upload' ? '页面上传' : '命令行导入'}</td>
              <td>{row.fileCount}</td>
              <td>{row.newCount}</td>
              <td>{row.dedupCount}</td>
              <td className={row.failedCount > 0 ? 'ing-bad' : undefined}>{row.failedCount}</td>
              <td>{row.mediaFound === 0 ? '—' : `${row.mediaFound}／新 ${row.mediaNew}`}</td>
              <td className="actions">
                <button
                  className="linkbtn"
                  type="button"
                  onClick={() => setOpenId(openId === row.batchId ? null : row.batchId)}
                >
                  {openId === row.batchId ? '收起' : '逐份'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {openId !== null && <BatchItems batchId={openId} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────

export function IngestionPage(): ReactNode {
  const [view, setView] = useState<'timeline' | 'ledger'>('timeline');
  const [summary, setSummary] = useState<PoolSummary | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    void api.ingestSummary().then(setSummary, () => undefined);
  }, [reloadKey]);

  return (
    <div className="content-in">
      <div className="page-head">
        <div>
          <h1>原始记录池</h1>
          <p className="sub-hint">
            跨 ATC 的原始资料，不属于任何一张卡。上传即去重、即提取内嵌图片——
            与命令行导入走同一条路。
          </p>
        </div>
        {summary !== null && (
          <p className="sub-hint ing-summary">
            共 {summary.total} 份 其中提取自文档 {summary.derived} 份 判出资料时间{' '}
            {summary.withContentTime}／{summary.total}
          </p>
        )}
      </div>

      <IntakePanel onDone={reload} />

      <div className="segmented ing-views" role="tablist" aria-label="记录池视图">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'timeline'}
          onClick={() => setView('timeline')}
        >
          时间轴
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'ledger'}
          onClick={() => setView('ledger')}
        >
          导入台账
        </button>
      </div>

      {view === 'timeline' ? (
        <TimelineView reloadKey={reloadKey} />
      ) : (
        <LedgerView reloadKey={reloadKey} />
      )}
    </div>
  );
}
